/**
 * Shared smart-summary helper for the Database Blueprint setup dialog's
 * "SummaryRow" lines (object types, schemas, exclusions, data blueprint).
 *
 * Rules:
 *  1. Empty selection         → "No <plural> selected" (or custom `emptyLabel`)
 *  2. All items selected      → "All <plural>" / "All N <plural>" (or `allLabel`)
 *  3. All-but-up-to-4 missing → "All besides X, Y, Z"
 *  4. Up-to-4 selected        → "Selected: X, Y, Z"
 *  5. Otherwise               → "N of M <plural>"
 *
 * Keeps the dialog readable at a glance — no per-row bespoke wording.
 */

export type BuildSelectionSummaryArgs = {
  /** Labels of currently selected items. */
  selectedLabels: ReadonlyArray<string>;
  /** Total number of items in the picker. */
  totalCount: number;
  /** Singular entity noun, e.g. "object type", "schema", "table". */
  entityNounSingular: string;
  /** Plural entity noun, e.g. "object types", "schemas", "tables". */
  entityNounPlural: string;
  /**
   * Override the default "All <plural>" wording, e.g. "All object types".
   * When `totalCount === 1` and no override is given, the helper still uses
   * the plural form for consistency ("All 1 schemas" reads oddly — callers
   * can pass `allLabel: \`All ${total} schema\`` if they care).
   */
  allLabel?: string;
  /** Override the default "No <plural> selected" wording. */
  emptyLabel?: string;
  /**
   * Optional list of labels of items that are NOT selected. When omitted, the
   * helper assumes it can't compute the unselected list (so rule 3 is skipped
   * and falls through to rule 5). Pass this when you want the "All besides X"
   * branch to fire.
   */
  unselectedLabels?: ReadonlyArray<string>;
  /**
   * Override the default ceiling on how many items rules 3 and 4 will list
   * inline. Default 4 keeps single-line rows scannable; the blueprint-data row
   * passes 10 because it allows the summary to wrap.
   */
  maxListed?: number;
};

const DEFAULT_MAX_LISTED = 4;

export function buildSelectionSummary(args: BuildSelectionSummaryArgs): string {
  const {
    selectedLabels,
    totalCount,
    entityNounPlural,
    allLabel,
    emptyLabel,
    unselectedLabels,
    maxListed = DEFAULT_MAX_LISTED,
  } = args;
  const selectedCount = selectedLabels.length;

  // Rule 1 — empty.
  if (selectedCount === 0) {
    return emptyLabel ?? `No ${entityNounPlural} selected`;
  }

  // Rule 2 — all selected.
  if (selectedCount >= totalCount && totalCount > 0) {
    return allLabel ?? `All ${totalCount} ${entityNounPlural}`;
  }

  // Rule 3 — all but up-to-maxListed missing.
  if (
    unselectedLabels &&
    unselectedLabels.length > 0 &&
    unselectedLabels.length <= maxListed
  ) {
    return `All besides ${unselectedLabels.join(', ')}`;
  }

  // Rule 4 — up-to-maxListed selected.
  if (selectedCount > 0 && selectedCount <= maxListed) {
    return `Selected: ${selectedLabels.join(', ')}`;
  }

  // Rule 5 — fallback count.
  return `${selectedCount} of ${totalCount} ${entityNounPlural}`;
}
